// ==============================
// 파일명 : src/components/users/UserAccountSyncManager.js
// 역할 : Firebase Authentication 계정을 먼저 생성하고, 생성된 UID를
//         Firestore users 컬렉션의 문서 ID로 사용하여 동기화
// 수정일 : 2025-10-14
// ==============================

import { useCallback } from "react";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import app from "../../lib/firebase";
import { USER_FIELD_AUTH_UID } from "../../constants/users/fields";
import { USER_ACCOUNT_SYNC } from "../../constants/users/accountSync";
import { normalizeAuthUid, normalizePackIds } from "../../utils/users";
import { doc, setDoc, getFirestore } from "firebase/firestore";
import { setUserOwnedPacks, deleteUserImmediately } from "../../lib/firestore";

export default function useUserAccountSyncManager() {
  const auth = getAuth(app);
  const db = getFirestore(app);

  const createUserAccount = useCallback(
    async ({ firestore = {}, auth: authPayload = {}, ownedPacks = [] }) => {
      try {
        const email = (authPayload.email || firestore.email || "").trim();
        const password = (authPayload.password || "").trim() || USER_ACCOUNT_SYNC.defaults.passwordFallback;
        const displayName = (authPayload.displayName || firestore.displayName || "").trim();

        console.log("1️⃣ Firebase Auth 사용자 생성 시작:", { email, displayName });

        // 1. Firebase Authentication에서 사용자 생성
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const createdUid = userCredential.user.uid;

        console.log("✅ Firebase Auth 사용자 생성 완료. UID:", createdUid);

        // 2. 생성된 UID를 문서 ID로 사용하여 Firestore에 저장
        const firestorePayload = {
          ...firestore,
          [USER_FIELD_AUTH_UID]: createdUid,
          email: email,
          displayName: displayName,
        };
        delete firestorePayload.password;
        delete firestorePayload.ownedPacks;

        console.log("2️⃣ Firestore 사용자 문서 저장 시작. 문서 ID:", createdUid);

        // Firestore에 문서 저장 (문서 ID = Auth UID)
        await setDoc(doc(db, "users", createdUid), firestorePayload);

        console.log("✅ Firestore 사용자 문서 저장 완료");

        // 3. 보유팩 저장
        const normalizedOwned = normalizePackIds(ownedPacks);
        if (normalizedOwned.length > 0) {
          console.log("3️⃣ 보유팩 저장 시작:", normalizedOwned);
          await setUserOwnedPacks(createdUid, normalizedOwned);
          console.log("✅ 보유팩 저장 완료");
        }

        console.log("🎉 사용자 생성 프로세스 완료. 최종 UID:", createdUid);

        return createdUid;
      } catch (error) {
        console.error("❌ 사용자 생성 실패:", error);

        let userMessage = "사용자 생성 중 오류가 발생했습니다.";
        if (error.code === "auth/email-already-in-use") {
          userMessage = "이미 사용 중인 이메일입니다.";
        } else if (error.code === "auth/invalid-email") {
          userMessage = "유효하지 않은 이메일 형식입니다.";
        } else if (error.code === "auth/weak-password") {
          userMessage = "비밀번호는 최소 6자 이상이어야 합니다.";
        } else if (error.message) {
          userMessage = error.message;
        }

        const enhancedError = new Error(userMessage);
        enhancedError.code = error.code;
        enhancedError.serverMessage = error.message;
        throw enhancedError;
      }
    },
    [auth, db]
  );

  const updateUserAccount = useCallback(
    async (userId, { firestore = {}, auth: authPayload = {}, ownedPacks = [] }) => {
      try {
        const targetUid = normalizeAuthUid(firestore[USER_FIELD_AUTH_UID], userId);
        if (!targetUid) {
          throw new Error("Firebase UID를 확인할 수 없습니다.");
        }

        console.log("🔄 사용자 정보 수정 시작. UID:", targetUid);

        // 1. Firestore 문서 업데이트
        const firestorePayload = {
          ...firestore,
          [USER_FIELD_AUTH_UID]: targetUid,
        };
        delete firestorePayload.password;
        delete firestorePayload.ownedPacks;

        await setDoc(doc(db, "users", targetUid), firestorePayload, { merge: true });

        console.log("✅ Firestore 문서 업데이트 완료");

        // 2. 보유팩 업데이트
        const normalizedOwned = normalizePackIds(ownedPacks);
        console.log("🔄 보유팩 업데이트:", normalizedOwned);
        await setUserOwnedPacks(targetUid, normalizedOwned);

        console.log("✅ 사용자 정보 수정 완료");

        return targetUid;
      } catch (error) {
        console.error("❌ 사용자 수정 실패:", error);
        const enhancedError = new Error(error.message || "사용자 정보 수정 중 오류가 발생했습니다.");
        enhancedError.code = error.code;
        throw enhancedError;
      }
    },
    [db]
  );

  const deleteUserAccount = useCallback(
    async (user = {}, options = {}) => {
      try {
        const targetUid = normalizeAuthUid(user[USER_FIELD_AUTH_UID], user.id);
        if (!targetUid) {
          throw new Error("Firebase UID를 확인할 수 없습니다.");
        }

        console.log("🗑️ 사용자 삭제 시작. UID:", targetUid);

        // Firestore에서 삭제 (구매 내역 포함)
        await deleteUserImmediately(targetUid);

        console.log("✅ 사용자 삭제 완료");

        return targetUid;
      } catch (error) {
        console.error("❌ 사용자 삭제 실패:", error);
        const enhancedError = new Error(error.message || "사용자 삭제 중 오류가 발생했습니다.");
        enhancedError.code = error.code;
        throw enhancedError;
      }
    },
    []
  );

  return {
    createUserAccount,
    updateUserAccount,
    deleteUserAccount,
  };
}
